import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AvailabilitySlot } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime, todayIso } from '@/lib/utils';
import { Alert, Button, Checkbox, DatePicker, Field, Select } from '@/components/ui/primitives';

export interface AffectedBooking {
  id: string;
  slotAt: string;
  patient: { id: string; fullName: string; mrn: string; phone: string | null };
}

export function planMoves(bookingIds: readonly string[], freeSlots: readonly string[]): { appointmentId: string; slotAt: string }[] {
  return bookingIds.slice(0, freeSlots.length).map((appointmentId, index) => ({ appointmentId, slotAt: freeSlots[index] }));
}

export function BulkMovePanel({
  bookings,
  doctorId,
  doctors,
  onDone,
}: {
  bookings: AffectedBooking[];
  doctorId: string;
  doctors: { id: string; name: string }[];
  onDone: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(bookings.map((booking) => booking.id)));
  const [targetDoctor, setTargetDoctor] = useState(doctorId);
  const [date, setDate] = useState(todayIso());
  const [notify, setNotify] = useState(true);
  const [result, setResult] = useState<{ moved: number; results: { appointmentId: string; ok: boolean; message?: string }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const availability = useQuery({
    queryKey: ['availability', targetDoctor, date],
    queryFn: () => get<AvailabilitySlot[]>(`/schedules/availability${qs({ doctorId: targetDoctor, date })}`),
    enabled: Boolean(targetDoctor && date),
  });
  const free = (availability.data ?? []).filter((slot) => slot.available).map((slot) => slot.startsAt);
  const chosen = bookings.filter((booking) => selected.has(booking.id)).map((booking) => booking.id);
  const moves = planMoves(chosen, free);

  const move = useMutation({
    mutationFn: () =>
      post<{ moved: number; results: { appointmentId: string; ok: boolean; message?: string }[] }>('/appointments/bulk-reschedule', {
        moves: moves.map((row) => ({ ...row, ...(targetDoctor !== doctorId ? { doctorId: targetDoctor } : {}) })),
        reason: 'Doctor on leave',
        notifyPatient: notify,
      }),
    onSuccess: setResult,
    onError: (caught) => setError(describeError(caught)),
  });

  if (result) {
    return (
      <div className="space-y-2">
        <Alert variant={result.moved === result.results.length ? 'success' : 'warning'}>
          {result.moved} {tr('opd', 'of')} {result.results.length} {tr('opd', 'bookings_moved')}
        </Alert>
        <ul className="space-y-1 text-sm">
          {result.results
            .filter((row) => !row.ok)
            .map((row) => (
              <li key={row.appointmentId}>
                {bookings.find((booking) => booking.id === row.appointmentId)?.patient.fullName}: {row.message}
              </li>
            ))}
        </ul>
        <Button onClick={onDone}>{tr('opd', 'done')}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Alert variant="warning">{bookings.length} {tr('opd', 'booking_s_fall_inside_this_leave_move_them_now')}</Alert>
      <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
        {bookings.map((booking) => (
          <li key={booking.id}>
            <Checkbox
              checked={selected.has(booking.id)}
              onCheckedChange={(checked) =>
                setSelected((current) => {
                  const next = new Set(current);
                  if (checked) next.add(booking.id);
                  else next.delete(booking.id);
                  return next;
                })
              }
              label={`${dateTime(booking.slotAt)} · ${booking.patient.fullName} · ${booking.patient.phone ?? 'no phone'}`}
            />
          </li>
        ))}
      </ul>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={tr('opd', 'move_to_doctor')}>
          <Select value={targetDoctor} onChange={(e) => setTargetDoctor(e.target.value)}>
            {doctors.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('opd', 'on')} hint={tr('opd', 'x_free_slot_s', { length: free.length })}>
          <DatePicker value={date} onChange={setDate} />
        </Field>
      </div>
      <Checkbox checked={notify} onCheckedChange={setNotify} label={tr('opd', 'send_the_patients_an_sms')} />
      {moves.length < chosen.length && <Alert variant="warning">{tr('opd', 'only')} {moves.length} {tr('opd', 'free_slot_s_that_day_the_rest_stay_where_they')}</Alert>}
      {error && <Alert variant="destructive">{error}</Alert>}
      <div className="flex gap-2">
        <Button onClick={() => move.mutate()} loading={move.isPending} disabled={moves.length === 0}>
          {tr('opd', 'move')} {moves.length}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {tr('opd', 'leave_them')}
        </Button>
      </div>
    </div>
  );
}
